import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table, { UtilBar } from '../components/common/Table.jsx';
import Tabs from '../components/common/Tabs.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { RecCell, SavingCell, RecommendedCostCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr, buildBaseCostKpis, exportCSV, buildExportFilename, ADD_NEW_ACCOUNT,
  setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';
import MultiSelect from '../components/common/MultiSelect.jsx';

import OptimizationService from '../services/OptimizationService';
import { useAuth } from '../state/AuthContext';
import useTagList from '../hooks/useTagList';

const tabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'AWS Recommendations' },
];

// ── Rec-type resolution ────────────────────────────────────
const resolveRDSRec = (apiRecType) => {
  const displayVal = apiRecType ?? '';
  const t = displayVal.toUpperCase().trim();

  let recPillClass = 'rec-rs';
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
    recPillClass = 'rec-ok';
  } else if (t === 'DELETE' || t === 'REMOVE' || t === 'UNATTACHED' || t === 'RELEASE' || t === 'TERMINATE') {
    recPillClass = 'rec-id';
  } else if (t === 'STOP' || t === 'STOPPED' || t === 'SCHEDULE') {
    recPillClass = 'rec-warn';
  } else if (t === 'BUY' || t === 'RI' || t.includes('RI') || t.includes('SAVINGS')) {
    recPillClass = 'rec-buy';
  }

  return {
    recType: displayVal,
    recPillClass
  };
};

// ── Unified Row Mapper ─────────────────────────────────────
const mapRDSItem = (item, index, source) => {
  const dbId = item.DBInstanceIdentifier ?? item.dBInstanceIdentifier ?? item.dbInstanceIdentifier ?? item.id;
  const engine = item.Engine ?? item.engine ?? '—';
  const engineVer = item.EngineVersion ?? item.engineVersion ?? '';
  const dbClass = item.DBInstanceClass ?? item.dBInstanceClass ?? item.dbInstanceClass ?? '—';
  const multiAZ = item.MultiAZ ?? item.multiAZ;
  const isMultiAZ = multiAZ === true || multiAZ === 'true' || multiAZ === 'Yes' ? 'Yes' : 'No';
  const status = item.DBInstanceStatus ?? item.dBInstanceStatus ?? item.status ?? '—';
  const cpu = item.AverageCPUValue ?? item.averageCPUValue ?? null;
  const peakCpu = item.PeakCPUValue ?? item.peakCPUValue ?? null;
  const p99Cpu = item.P99CPUValue ?? item.p99CPUValue ?? null;
  const p95Cpu = item.P95CPUValue ?? item.p95CPUValue ?? null;
  const connections = item.AvgConnectionsValue ?? item.avgConnectionsValue ?? item.ConnectionsUsed ?? item.connectionsUsed ?? null;
  const storage = item.AllocatedStorage ?? item.allocatedStorage ?? '—';
  const storageType = item.StorageType ?? item.storageType ?? '';

  let current, recommended;
  if (source === 'custom') {
    current = parseCost(item.CurrentInstanceMonthlyCostValue ?? item.currentInstanceMonthlyCostValue);
    recommended = parseCost(item.CustomRecomendedRDSOnDemandMonthlyPriceValue ?? item.customRecomendedRDSOnDemandMonthlyPriceValue);
  } else if (source === 'aws') {
    current = parseCost(item.CurrentInstanceMonthlyCostValue ?? item.currentInstanceMonthlyCostValue);
    recommended = parseCost(item.RecommendedInstanceMonthlyCostValue ?? item.recommendedInstanceMonthlyCostValue ?? 0);
  } else {
    // combined — uses camelCase keys
    current = parseCost(item.currentInstanceMonthlyCostValue ?? item.CurrentInstanceMonthlyCostValue);
    recommended = parseCost(item.recommendedRDSOnDemandMonthlyPriceValue ?? item.recommendedInstanceMonthlyCostValue ?? 0);
  }

  // Backend savingCost is authoritative (API sends both SavingCost and savingCost across sources)
  const apiSaving = item.SavingCost ?? item.savingCost;
  const saving = apiSaving != null
    ? parseCost(apiSaving)
    : (current > recommended && recommended > 0 ? current - recommended : 0);

  // Remark: handle both capitalisation variants; treat "NA" as absent
  const rawRemark = item.CustomRecomendationRemark ?? item.customRecomendationRemark ?? item.customRecommendationRemark ?? '';
  const remarkClean = rawRemark.toUpperCase().trim() === 'NA' ? '' : rawRemark.trim();

  // Detail: primary info line shown under the pill
  const rawDetail = item.RecommendationDetail ?? item.recommendationDetail ?? '';
  const detailClean = (rawDetail.toLowerCase() === 'optimal' || rawDetail.toLowerCase() === 'na') ? '' : rawDetail;

  // Recommended instance class (shows as "→ db.x.large")
  const recClass = item.CustomRecomendedRDSInstance ?? item.RecommendedInstanceClass ?? item.recommendedInstanceClass ?? '';
  const recLabel = recClass && recClass !== 'NA' && recClass !== '' ? `→ ${recClass}` : '';

  // Follow EC2 pattern: pill remark = customRemark if present, else fall back to detail
  const recommendationRemark = remarkClean || detailClean;

  const rawRecType = item.RecommendationType ?? item.recommendationType ?? '';
  const { recType, recPillClass } = resolveRDSRec(rawRecType);

  return {
    id: dbId || `${source}-${index}`,
    dbIdentifier: dbId || '—',
    engine: engineVer ? `${engine} ${engineVer}` : engine,
    class: dbClass,
    multiAZ: isMultiAZ,
    status,
    cpu,
    peakCpu,
    p99Cpu,
    p95Cpu,
    connections: connections != null ? String(connections) : '—',
    storage: storage !== '—' ? `${storage} GiB${storageType ? ` ${storageType}` : ''}` : '—',
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    savingValue: saving,
    recommendationRemark,
    recType,
    recPillClass,
    recLabel,
    saving: formatSavingStr(saving),
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
    StartMonth: item.StartMonth ?? '—',
    tags: item.Tags ?? item.tags ?? [],
    checked: false,
  };
};

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue, totalInstances, rows = []) => {
  const current = parseCost(totalPriceValue?.currentCost);
  const recommended = parseCost(totalPriceValue?.recommendedCost);
  const saving = parseCost(totalPriceValue?.savingCost);
  const availableCount = rows.filter((r) => (r.status ?? '').toLowerCase().includes('available') || (r.status ?? '').toLowerCase().includes('running')).length;
  const multiAZCount = rows.filter((r) => r.multiAZ === 'Yes').length;
  return [
    ...buildBaseCostKpis({
      countTitle: 'Total Instances',
      countValue: totalInstances,
      countSubtitle: `${availableCount} available · ${multiAZCount} Multi-AZ`,
      current, saving, recommended,
    }),
  ];
};

// ── Chart Builders ─────────────────────────────────────────
const buildTrendChartData = (rows) => {
  const active = rows
    .filter((r) => r.costValue > 0)
    .map((r) => ({ name: r.dbIdentifier, cpu: r.cpu ?? 0, cost: (r.costValue ?? 0) / 30 }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 10);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.name),
    datasets: [
      { label: 'Avg CPU %', data: active.map((r) => r.cpu), borderColor: '#2196f3', backgroundColor: 'rgba(33,150,243,0.18)', fill: true, tension: 0.3, yAxisID: 'y1', borderWidth: 2, pointRadius: 3 },
      { label: 'Daily Cost ($)', data: active.map((r) => parseFloat(r.cost.toFixed(2))), borderColor: '#f57c00', fill: false, tension: 0.3, yAxisID: 'y2', borderWidth: 2, pointRadius: 3, borderDash: [4, 3] },
    ],
  };
};

const buildEngineDistChartData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => { const e = (r.engine || 'Unknown').split(' ')[0]; map[e] = (map[e] || 0) + r.costValue; });
  const labels = Object.keys(map);
  if (!labels.reduce((s, l) => s + map[l], 0)) return null;
  const colors = ['#2196f3', '#f57c00', '#2ea66f', '#8e24aa', '#ec407a'];
  return { labels, datasets: [{ data: labels.map((l) => parseFloat(map[l].toFixed(2))), backgroundColor: labels.map((_, i) => colors[i % colors.length]), borderWidth: 2, borderColor: '#fff' }] };
};

const buildClassChartData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => { if (r.class) map[r.class] = (map[r.class] || 0) + r.costValue; });
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.reduce((s, [, v]) => s + v, 0)) return null;
  return { labels: sorted.map(([c]) => c), datasets: [{ data: sorted.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: '#2196f3', borderRadius: 4, barPercentage: 0.6 }] };
};

const buildRiCoverageChartData = () => null; // No RI/Savings Plan coverage API — do not fabricate from row rec types

const buildCpuProfileChartData = (rows) => {
  const active = rows
    .filter((r) => r.cpu != null || r.peakCpu != null)
    .sort((a, b) => (b.peakCpu ?? b.cpu ?? 0) - (a.peakCpu ?? a.cpu ?? 0))
    .slice(0, 8);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.dbIdentifier),
    datasets: [
      { label: 'Avg',  data: active.map((r) => r.cpu     ?? 0), backgroundColor: '#2196f3', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P95',  data: active.map((r) => r.p95Cpu  ?? 0), backgroundColor: '#26c6da', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P99',  data: active.map((r) => r.p99Cpu  ?? 0), backgroundColor: '#7e57c2', borderRadius: 3, barPercentage: 0.75 },
      { label: 'Peak', data: active.map((r) => r.peakCpu ?? 0), backgroundColor: '#f57c00', borderRadius: 3, barPercentage: 0.75 },
    ],
  };
};


const buildInsightActions = (rows) => {
  const seen = new Set();
  const withSaving = rows
    .filter((r) => {
      const key = `${r.dbIdentifier}|${r.recType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return parseSaving(r.saving) > 0;
    })
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →';
    let actionClass = 'mini-btn';
    if (r.recType === 'SCHEDULE') actionLabel = 'Schedule →';
    else if (r.recType === 'BUY 3-YR RI') actionLabel = 'Buy RI →';
    else if (r.recType === 'DOWNSIZE') { actionLabel = 'Downsize →'; actionClass = 'mini-btn ghost'; }
    return { id: i + 1, title: r.dbIdentifier || r.id, amount: r.saving, desc: `${r.engine} (${r.class}) ${r.recLabel} · ${r.recommendationRemark}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Main Component ──────────────────────────────────────────
const RDSOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAWS, setIsLoadingAWS] = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [awsRows, setAwsRows] = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [awsKpi, setAwsKpi] = useState([]);

  const [trendData, setTrendData] = useState(null);
  const [engineDistData, setEngineDistData] = useState(null);
  const [classData, setClassData] = useState(null);
  const [riCoverageData, setRiCoverageData] = useState(null);
  const [cpuProfileData, setCpuProfileData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const { tagList } = useTagList('RDS_DBINSTANCES');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });
  const [engineFilter, setEngineFilter] = useState('All engines');
  const [classFilter, setClassFilter] = useState('All');

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const handleDetailsClick = (row) => {
    setAiData({
      dbInstanceIdentifier: row.dbIdentifier, engine: row.engine, instanceClass: row.class,
      status: row.status, multiAZ: row.multiAZ, cpuAvg14dPercent: row.cpu,
      connections: row.connections, storage: row.storage, currentMonthlyCost: row.cost,
      recommendation: row.recType, recommendedClass: row.recLabel, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt('You are a cloud cost advisor. Based on the RDS instance data provided, give a short, friendly, and natural insight about the database cost situation, the recommended action, and potential monthly savings.');
    setAiOpen(true);
  };

  const toggleRowChecked = (id) => {
    const updater = (rows) => rows.map((r) => r.id === id ? { ...r, checked: !r.checked } : r);
    if (activeTab === 0) setCombinedRows(updater);
    else if (activeTab === 1) setCustomRows(updater);
    else setAwsRows(updater);
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (tabIndex === 0 && isLoadingCombined) return;
    if (tabIndex === 1 && isLoadingCustom) return;
    if (tabIndex === 2 && isLoadingAWS) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    if (tabIndex === 0) {
      setIsLoadingCombined(true);
      setCombinedRows([]);
      OptimizationService.getTabularData(selectedAccount, 'COMBINED_RDS', null, isForceRefresh)
        .then((data) => {
          const rows = (data?.rows ?? []).map((item, i) => mapRDSItem(item, i, 'combined'));
          setCombinedRows(rows);
          setCombinedKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, rows));
        })
        .finally(() => setIsLoadingCombined(false));
    } else if (tabIndex === 1) {
      setIsLoadingCustom(true);
      setCustomRows([]);
      OptimizationService.getTabularData(selectedAccount, 'CUSTOM_RDS', null, isForceRefresh)
        .then((data) => {
          const rows = (data?.rows ?? []).map((item, i) => mapRDSItem(item, i, 'custom'));
          setCustomRows(rows);
          setCustomKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, rows));
        })
        .finally(() => setIsLoadingCustom(false));
    } else {
      setIsLoadingAWS(true);
      setAwsRows([]);
      OptimizationService.getTabularData(selectedAccount, 'RDS', null, isForceRefresh)
        .then((data) => {
          const rows = (data?.rows ?? []).map((item, i) => mapRDSItem(item, i, 'aws'));
          setAwsRows(rows);
          setAwsKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, rows));
        })
        .finally(() => setIsLoadingAWS(false));
    }
  };

  const handleTabChange = (i) => { setActiveTab(i); fetchTabData(i); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchedTabsRef.current = new Set(); fetchTabData(0); } }, [selectedAccount]);

  const filteredCustomRows = useMemo(() => {
    if (!filterList.tag || filterList.values.length === 0) return customRows;
    return customRows.filter((row) => {
      const tags = row.tags ?? [];
      return tags.some((t) => (t.Key ?? t.key) === filterList.tag && filterList.values.includes(t.Value ?? t.value));
    });
  }, [customRows, filterList]);

  const currentRows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;

  const uniqueEngines = [...new Set(currentRows.map((r) => r.engine.split(' ')[0]).filter(Boolean))].sort();
  const uniqueClasses = [...new Set(currentRows.map((r) => r.class).filter((c) => c && c !== '—'))].sort();

  const filteredRows = (() => {
    const seen = new Set();
    return currentRows.filter((row) => {
      if (engineFilter !== 'All engines' && !row.engine.toLowerCase().includes(engineFilter.toLowerCase())) return false;
      if (classFilter !== 'All' && row.class !== classFilter) return false;
      const key = [
        row.dbIdentifier, row.engine, row.class, row.multiAZ, row.status,
        row.cpu, row.peakCpu, row.p99Cpu, row.p95Cpu, row.storage,
        row.cost, row.recType, row.recPillClass, row.recLabel,
        row.recommendationRemark, row.saving, row.recommendedCost,
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  useEffect(() => {
    const rows = (activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows)
      .filter((row) => {
        if (engineFilter !== 'All engines' && !row.engine.toLowerCase().includes(engineFilter.toLowerCase())) return false;
        if (classFilter !== 'All' && row.class !== classFilter) return false;
        return true;
      });
    if (rows.length > 0) {
      setTrendData(buildTrendChartData(rows));
      setEngineDistData(buildEngineDistChartData(rows));
      setClassData(buildClassChartData(rows));
      setRiCoverageData(buildRiCoverageChartData(rows));
      setCpuProfileData(buildCpuProfileChartData(rows));
      const { actions, totalSaving } = buildInsightActions(rows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setTrendData(null); setEngineDistData(null); setClassData(null); setRiCoverageData(null);
      setCpuProfileData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [activeTab, combinedRows, filteredCustomRows, awsRows, engineFilter, classFilter]);

  const activeKpi = activeTab === 0 ? combinedKpi : activeTab === 1 ? customKpi : awsKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAWS;

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    const rows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'RDS Optimization',
      kpi: activeKpi,
      rdsRecommendations: rows,
    });
  }, [activeTab, combinedRows, filteredCustomRows, awsRows]);

  const selectedCount = filteredRows.filter((r) => r.checked).length;
  const selectedSaving = filteredRows.filter((r) => r.checked).reduce((sum, r) => sum + (r.savingValue || 0), 0);
  const allChecked = filteredRows.length > 0 && filteredRows.every((r) => r.checked);

  const toggleAllChecked = () => {
    const allNowChecked = allChecked;
    const updater = (rList) => rList.map((r) => ({ ...r, checked: !allNowChecked }));
    if (activeTab === 0) setCombinedRows(updater);
    else if (activeTab === 1) setCustomRows(updater);
    else setAwsRows(updater);
  };

  const rdsColumns = [
    {
      key: 'checkbox', label: <span className={`checkbox ${allChecked ? 'checked' : ''}`} onClick={toggleAllChecked} />, className: 'center',
      render: (row) => <span className={`checkbox ${row.checked ? 'checked' : ''}`} onClick={(e) => { e.stopPropagation(); toggleRowChecked(row.id); }} />,
    },
    { key: 'dbIdentifier', label: 'DB Identifier' },
    { key: 'engine', label: 'Engine' },
    { key: 'class', label: 'Class' },
    { key: 'multiAZ', label: 'Multi-AZ' },
    {
      key: 'status', label: 'Status',
      render: (row) => <span className={`status-pill ${row.status.toLowerCase() === 'available' || row.status.toLowerCase() === 'running' ? 'st-avail' : 'st-stopped'}`}>{row.status}</span>,
    },
    { key: 'cpu', label: 'CPU Avg', render: (row) => <UtilBar avgValue={row.cpu} peakValue={row.peakCpu} /> },
    { key: 'p99Cpu', label: 'P99 CPU', className: 'num', render: (row) => row.p99Cpu != null ? <span>{row.p99Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
    { key: 'p95Cpu', label: 'P95 CPU', className: 'num', render: (row) => row.p95Cpu != null ? <span>{row.p95Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
    // { key: 'connections', label: 'Connections' }, // Connections and MaxConnections — commented out: field data not yet available
    { key: 'storage', label: 'Storage', className: 'num' },
    // { key: 'StartMonth', label: 'Backup' },
    { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <span className="td-cost-bold">{row.cost}</span> },
    { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
    { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
    { key: 'action', label: 'Action', render: (row) => <button className={row.actionClass} onClick={() => handleDetailsClick(row)}>{row.actionLabel}</button> },
  ];

  const handleExport = () =>
    exportCSV(filteredRows, rdsColumns.filter((c) => typeof c.label === 'string' && !['checkbox', 'cpu'].includes(c.key)), buildExportFilename(`rds_database_optimization_${['combined', 'cloudsanalytics', 'aws'][activeTab] ?? activeTab}`, selectedAccount));

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>RDS</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            RDS Database Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{filteredRows.length} instances</span>}
          </h1>
          <div className="meta">Rightsize, schedule, and buy reserved capacity for relational databases</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={filteredRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchTabData(activeTab, true)} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      <div className="grid-3">
        {/* RDS Utilization & Cost (Last 30 Days) — commented out: graph data not yet available */}
        <Card className="first" title="RDS utilization & cost" subtitle="CPU utilization vs daily cost (Top 10 databases)">
          <LineChart data={trendData} loading={isLoadingActive} chartType="line" y2BeginAtZero={false} xMaxRotation={0} y1Label="" y2Label="Daily Cost ($)" />
        </Card>
        <Card className="first" title="Engine distribution" subtitle="Cost share by engine">
          <DonutChart data={engineDistData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard title="Top RDS actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName="instances" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <Tabs tabs={tabs} defaultActive={0} onTabChange={handleTabChange} />

      <div className="filter-bar">
          {activeTab === 1 && (
            <>
              <div className="field">
                <label>Filter Tag</label>
                <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
                  <option value="">None</option>
                  {tagList.map((tag) => <option key={tag.Key} value={tag.Key}>{tag.Key}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Filter Tag Values</label>
                <MultiSelect
                  options={tagList.find((t) => t.Key === filterList.tag)?.Value ?? []}
                  value={filterList.values}
                  onChange={(vals) => handleFilterChange('values', vals)}
                  disabled={!filterList.tag}
                />
              </div>
            </>
          )}
          {/* <div className="field">
            <label>Engine</label>
            <select value={engineFilter} onChange={(e) => setEngineFilter(e.target.value)}>
              <option value="All engines">All engines</option>
              {uniqueEngines.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div> */}
          {/* <div className="field">
            <label>Instance Class</label>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="All">All</option>
              {uniqueClasses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div> */}
          <div className="spacer" />
          <button className="btn ghost" onClick={() => {
            setEngineFilter('All engines');
            setClassFilter('All');
            if (activeTab === 1) setFilterList({ tag: '', values: [] });
          }}>✕ Clear</button>
          <button className="btn primary" onClick={() => fetchTabData(activeTab, true)} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>

      {selectedCount > 0 && (
        <div className="bulk-bar">
          <span><strong>{selectedCount} instances selected</strong> · ${selectedSaving.toFixed(2)}/mo combined savings</span>
          <button className="btn ghost">⏰ Schedule Shutdown</button>
          <button className="btn ghost">↻ Rightsize</button>
          <button className="btn ghost">⌬ Buy RI</button>
          <button className="btn primary">⚡ Apply</button>
        </div>
      )}

      <Table
        key={activeTab}
        columns={rdsColumns}
        rows={filteredRows}
        loading={isLoadingActive}
        defaultPageSize={25}
        showToolbar={true}
        onExport={handleExport}
        defaultSortKey="saving"
        defaultSortDir="desc"
        emptyText="No RDS instances found."
        subscriptionGated
      />

      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '20px', marginBottom: '4px' }}>
        Utilization
      </div>
      <div className="grid-2 grid-gap-top">
        <Card title="CPU profile — Avg / P95 / P99 / Peak" subtitle="Headroom analysis per instance">
          <GroupedBarChart data={cpuProfileData} loading={isLoadingActive} />
        </Card>
        <Card title="Cost by instance class" subtitle="Top families this month">
          <HorizontalBarChart data={classData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default RDSOptimization;
